// utils/renderMessages.ts
import React from "react";
import Markdown from "markdown-to-jsx";
import { processMessageWithCitations } from "@shared/components/Assistant/MessageProcessor";
import { overrides } from "@shared/components/Assistant/MarkdownConfig";
import { convertS3UrlToHttps } from "./utils";
import { buildMessageFromParts } from "./messageParts";

const IMG_TOKEN_REGEX = /\{\[IMG:(\d+)\]\}/g;

type ModalDeps = {
  setShowDocxModal: (v: boolean) => void;
  setDocxContent: (v: string) => void;
  setShowExcelModal: (v: boolean) => void;
  setExcelData: (v: any[][]) => void;
  showAndSetModalImage: (src: string, alt: string) => void;
};

function renderProcessedChunk(node: React.ReactNode) {
  if (React.isValidElement(node)) return node;
  if (typeof node === "string") {
    return <Markdown options={{ overrides }}>{node}</Markdown>;
  }
  return <div className="text-red-500">⚠️ Invalid message format</div>;
}

function buildInlineNodesForHistory(opts: {
  textWithTokens: string;
  citations: any[];
  images: any[] | undefined;
  deps: ModalDeps;
}) {
  const { textWithTokens, citations, images, deps } = opts;
  const {
    setShowDocxModal,
    setDocxContent,
    setShowExcelModal,
    setExcelData,
    showAndSetModalImage,
  } = deps;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = IMG_TOKEN_REGEX.exec(textWithTokens)) !== null) {
    const before = textWithTokens.slice(lastIndex, match.index);
    if (before) {
      const processed = processMessageWithCitations({
        message: before,
        citations,
        setShowDocxModal,
        setDocxContent,
        setShowExcelModal,
        setExcelData,
        showDocxModal: false,
        docxContent: "",
        showExcelModal: false,
        excelData: [],
      });
      nodes.push(renderProcessedChunk(processed));
    }

    const idx = Number.parseInt(match[1], 10);
    const imgObj = images?.[idx];
    const src = convertS3UrlToHttps(imgObj);
    const alt = imgObj?.filename || `image-${idx}`;

    nodes.push(
      <img
        key={`hist-inline-img-${idx}-${match.index}`}
        src={src || ""}
        alt={alt}
        className="inline-block align-middle max-w-[65%] rounded border border-gray-200 p-2 mb-6 mx-1 hover:border-gray-300 transition-colors shadow cursor-pointer"
        onClick={() => showAndSetModalImage(src || "", alt)}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  const after = textWithTokens.slice(lastIndex);
  if (after) {
    const processed = processMessageWithCitations({
      message: after,
      citations,
      setShowDocxModal,
      setDocxContent,
      setShowExcelModal,
      setExcelData,
      showDocxModal: false,
      docxContent: "",
      showExcelModal: false,
      excelData: [],
    });
    nodes.push(renderProcessedChunk(processed));
  }

  return nodes;
}

/**
 * Hydrate stored messages for display.
 * - If message_parts exist → convert to tokens (CIT + IMG) and interleave images inline.
 * - If legacy with only citations → render text+badges; if text already has {[IMG:i]} tokens, inline them too.
 */
export function renderMessagesForDisplay(messages: any[], deps: ModalDeps) {
  return messages.map((msg) => {
    // NEW PATH: message_parts (history supports images + citations)
    if (
      msg?.sender === "bot" &&
      Array.isArray(msg.message_parts) &&
      msg.message_parts.length > 0
    ) {
      const { text: textWithTokens, badges } = buildMessageFromParts(
        msg.message_parts,
        msg.project_id ?? null
      );

      const nodes = buildInlineNodesForHistory({
        textWithTokens,
        citations: badges,
        images: msg.generated_images_s3_keys,
        deps,
      });

      return { ...msg, message: nodes };
    }

    // LEGACY PATH: citations array only (no positional images unless tokens already exist)
    if (
      msg?.sender === "bot" &&
      Array.isArray(msg.citations) &&
      msg.citations.length > 0
    ) {
      const text = String(msg.message ?? "");

      if (/\{\[IMG:\d+\]\}/.test(text)) {
        const nodes = buildInlineNodesForHistory({
          textWithTokens: text,
          citations: msg.citations,
          images: msg.generated_images_s3_keys,
          deps,
        });
        return { ...msg, message: nodes };
      }

      return {
        ...msg,
        message: processMessageWithCitations({
          message: text,
          citations: msg.citations,
          setShowDocxModal: deps.setShowDocxModal,
          setDocxContent: deps.setDocxContent,
          setShowExcelModal: deps.setShowExcelModal,
          setExcelData: deps.setExcelData,
          showDocxModal: false,
          docxContent: "",
          showExcelModal: false,
          excelData: [],
        }),
      };
    }

    // Plain message
    return msg;
  });
}
